import { useState, useEffect, FC, useRef } from 'react';
import { Card } from "@/components/ui/card";
import React from 'react';
import { Button } from "@/components/ui/button";
import { LiquidButton } from "@/components/ui/liquid-glass-button";
import { Check, ChevronDown, Eye, Share } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";

interface TimerProps {
  isRunning: boolean;
  isComplete: boolean;
  onReset: () => void;
  onTimerStop: (time: number) => void;
  onCheck?: () => void;
  onReveal?: (type: 'square' | 'word' | 'puzzle') => void;
  onShare?: () => void;
  isMobile?: boolean;
  interestTopic: string;
  shareUrl: string;
}

const Timer = ({ isRunning, isComplete, onReset, onTimerStop, onCheck, onReveal, onShare, isMobile, interestTopic, shareUrl }: TimerProps) => {
  const [seconds, setSeconds] = useState(0);
  const [isCopied, setIsCopied] = useState(false);
  const [progress, setProgress] = useState(0);
  const animationFrameRef = useRef<number | null>(null);
  const startTimeRef = useRef<number | null>(null);

  const updateTimer = () => {
    if (startTimeRef.current) {
      const elapsedSeconds = Math.floor((Date.now() - startTimeRef.current) / 1000);
      setSeconds(elapsedSeconds);
    }
    animationFrameRef.current = requestAnimationFrame(updateTimer);
  };

  useEffect(() => {
    if (isRunning && !isComplete) {
      // Set the start time if not already set
      if (!startTimeRef.current) {
        startTimeRef.current = Date.now() - (seconds * 1000);
      }
      
      // Start the animation frame loop
      animationFrameRef.current = requestAnimationFrame(updateTimer);
    } else if (isComplete && seconds > 0) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      onTimerStop(seconds);
    } else if (!isRunning) {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
      startTimeRef.current = null;
    }
    
    return () => {
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
        animationFrameRef.current = null;
      }
    };
  }, [isRunning, isComplete, onTimerStop]);

  // Reset timer when onReset is called
  useEffect(() => {
    if (!isRunning) {
      setSeconds(0);
      startTimeRef.current = null;
    }
  }, [isRunning]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const remainingSeconds = time % 60;
    return `${minutes.toString().padStart(2, '0')}:${remainingSeconds.toString().padStart(2, '0')}`;
  };

  const handleShare = async () => {
    if (onShare) {
      // Check if Web Share API is available (typically on mobile devices)
      if (isMobile && navigator.share) {
        try {
          await navigator.share({
            title: 'Crossword Puzzle',
            text: `Check out my custom crossword \"${interestTopic}\"!`,
            url: shareUrl
          });
        } catch (error) {
          console.error('Error sharing:', error);
        }
      } else {
        // Fallback to original share behavior for desktop
        await onShare();
        setIsCopied(true);
        setProgress(100);
        setTimeout(() => {
          setProgress(0);
        }, 50);
        setTimeout(() => {
          setIsCopied(false);
          setProgress(0);
        }, 4000);
      }
    }
  };

  const handleCheck = () => {
    onCheck?.();
  };

  const revealDropdownItems = [
    {
      label: 'Reveal Square',
      onClick: () => onReveal?.('square')
    },
    {
      label: 'Reveal Word',
      onClick: () => onReveal?.('word')
    },
    {
      label: 'Reveal Puzzle',
      onClick: () => onReveal?.('puzzle')
    }
  ];

  return (
    <div className="flex items-center w-full pr-2 sm:pr-0">
      <div className={`text-lg sm:text-xl md:text-2xl font-mono font-bold ${isMobile ? 'text-white' : ''} pl-2 sm:pl-0`}>{formatTime(seconds)}</div>
      <div className="flex-1 flex justify-center gap-1 sm:gap-2">
        {isMobile ? (
          <>
            <Button 
              variant="outline" 
              size="sm"
              onClick={onCheck}
              className="flex items-center gap-1 sm:gap-2 h-7 sm:h-10 text-sm"
            >
              <Check className="h-3 w-3 sm:h-4 sm:w-4" />
              <span>Check</span>
            </Button>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button 
                  variant="outline" 
                  size="sm"
                  className="flex items-center gap-1 sm:gap-2 h-7 sm:h-10 text-sm"
                >
                  <Eye className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Reveal</span>
                  <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent>
                <DropdownMenuItem onClick={() => onReveal?.('square')}>
                  Reveal Square
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onReveal?.('word')}>
                  Reveal Word
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onReveal?.('puzzle')}>
                  Reveal Puzzle
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </>
        ) : (
          <>
            <LiquidButton 
              variant="default" 
              size="sm"
              onClick={onCheck}
              className="flex items-center justify-center gap-1 sm:gap-2 h-7 sm:h-10 text-sm px-6 sm:px-8"
            >
              <Check className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="whitespace-nowrap">Check</span>
            </LiquidButton>

            <LiquidButton 
              variant="default" 
              size="sm"
              dropdown={true}
              dropdownItems={revealDropdownItems}
              className="flex items-center justify-center gap-1 sm:gap-2 h-7 sm:h-10 text-sm px-6 sm:px-8"
            >
              <Eye className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span className="whitespace-nowrap">Reveal</span>
              <ChevronDown className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
            </LiquidButton>
          </>
        )}
      </div>
      {onShare && (
        isMobile ? (
          <Button 
            variant="default" 
            size="sm"
            onClick={handleShare}
            className="flex items-center justify-center gap-1 sm:gap-2 bg-[#305CDE] hover:bg-[#000080] px-2 sm:px-5 transition-all duration-300 ease-out relative overflow-hidden h-7 sm:h-9 w-[60px] sm:w-[100px] text-xs sm:text-sm"
          >
            <div className="relative z-10 flex items-center justify-center gap-1 sm:gap-2 w-full">
              {!isCopied && <Share className="h-3 w-3 sm:h-4 sm:w-4" />}
              <span className={`transition-opacity duration-300 text-xs sm:text-sm`}>
                {isCopied ? "URL Copied!" : "Share"}
              </span>
            </div>
            {isCopied && (
              <div 
                className="absolute left-0 top-0 h-full bg-secondary/45"
                style={{ 
                  width: `${progress}%`,
                  transition: 'width 4s ease-out'
                }}
              />
            )}
          </Button>
        ) : (
          <LiquidButton
            variant="share"
            size="sm"
            onClick={handleShare}
            className="flex items-center justify-center gap-1 sm:gap-2 px-2 sm:px-5 transition-all duration-300 ease-out relative overflow-hidden h-7 sm:h-9 w-[60px] sm:w-[100px] text-xs sm:text-sm"
          >
            <div className="relative z-10 flex items-center justify-center gap-1 sm:gap-2 w-full">
              {!isCopied && <Share className="h-3 w-3 sm:h-4 sm:w-4" />}
              <span className={`transition-opacity duration-300 text-xs sm:text-sm text-[#1e3a8a]`}>
                {isCopied ? "URL Copied!" : "Share"}
              </span>
            </div>
            {isCopied && (
              <div 
                className="absolute inset-0 h-full w-full rounded-full overflow-hidden pointer-events-none"
              >
                <div
                  className="h-full bg-blue-600/30 rounded-full"
                  style={{
                    width: `${progress}%`,
                    transition: 'width 4s ease-out'
                  }}
                />
              </div>
            )}
          </LiquidButton>
        )
      )}
    </div>
  );
};

export default Timer;
